/**
 * ProgramLandingPage.js — /programs/:slug
 *
 * Bir programin landing sayfasi:
 *   - Programin renkleri/ikonu ile hero
 *   - Aciklama + feature badge'leri
 *   - Etiketli kurslar listesi (program_slug = bu programa esit)
 *   - "Tum Kurslar" linki ile filtreli katalog
 */
import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  Plane, Users, Brain, Languages, Briefcase, Book,
  GraduationCap, Heart, Code, Music, ArrowLeft, WifiOff, ArrowRight,
} from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { useProgram } from '@/hooks/usePrograms';
import { useLanguage } from '@/contexts/LanguageContext';
import { apiClient } from '@/services/apiClient';
import Navbar from '@/components/Navbar';
import { staggerContainer, cardHover, fadeUp, maybe } from '@/lib/motionVariants';

const ICON_MAP = { Plane, Users, Brain, Languages, Briefcase, Book, GraduationCap, Heart, Code, Music };

function IconFor({ name, color, size = 40 }) {
  const Comp = ICON_MAP[name] || Book;
  return <Comp size={size} color={color} strokeWidth={2.2} aria-hidden="true" />;
}

/* ---------- Full-page skeleton shown while program data is loading ---------- */
function ProgramLandingSkeleton() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      {/* Hero skeleton */}
      <div className="border-b-2 border-[var(--duo-stroke)] bg-[var(--duo-sky)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="h-4 w-32 bg-white/50 rounded animate-pulse mb-4" />
          <div className="flex items-start gap-5">
            <div className="w-20 h-20 rounded-2xl bg-white/60 animate-pulse shrink-0" />
            <div className="flex-1 space-y-3">
              <div className="h-8 w-64 bg-white/60 rounded animate-pulse" />
              <div className="h-4 w-full max-w-xl bg-white/40 rounded animate-pulse" />
              <div className="h-4 w-3/4 bg-white/40 rounded animate-pulse" />
            </div>
          </div>
        </div>
      </div>
      {/* Course cards skeleton */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="h-7 w-48 bg-[var(--duo-stroke)] rounded animate-pulse mb-6" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="card-duo animate-pulse h-40" />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ProgramLandingPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const reduced = useReducedMotion();
  const { data: program, isLoading, isError } = useProgram(slug);
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [coursesError, setCoursesError] = useState(false);

  useEffect(() => {
    document.body.classList.add('duo-page');
    return () => document.body.classList.remove('duo-page');
  }, []);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    async function loadCourses() {
      setCoursesLoading(true);
      setCoursesError(false);
      try {
        const res = await apiClient.get('/courses', { params: { program_slug: slug } });
        if (!cancelled) {
          const list = res.data?.courses ?? res.data ?? [];
          setCourses(Array.isArray(list) ? list : []);
        }
      } catch {
        if (!cancelled) {
          setCourses([]);
          setCoursesError(true);
        }
      } finally {
        if (!cancelled) setCoursesLoading(false);
      }
    }
    loadCourses();
    return () => { cancelled = true; };
  }, [slug]);

  if (isLoading) {
    return <ProgramLandingSkeleton />;
  }

  if (isError || !program) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-5xl mx-auto p-8 text-center" data-testid="program-not-found">
          <p className="text-[var(--text-muted)] mb-4">{t('programLanding.notFound')}</p>
          <button
            onClick={() => navigate('/programs')}
            className="btn-duo-blue"
          >
            {t('programLanding.allPrograms')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid={`program-page-${slug}`}>
      <Navbar />

      {/* Hero */}
      <div
        className="border-b-2 relative overflow-hidden"
        style={{ backgroundColor: program.color_accent, borderColor: program.color_primary }}
      >
        {/* Decorative blob */}
        <div
          className="absolute -top-16 -right-16 w-64 h-64 rounded-full opacity-10 pointer-events-none"
          style={{ backgroundColor: program.color_primary }}
          aria-hidden="true"
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 relative">
          <button
            onClick={() => navigate('/programs')}
            aria-label={t('programLanding.backAriaLabel')}
            className="inline-flex items-center gap-1 text-sm font-bold mb-4 hover:underline"
            style={{ color: program.color_primary }}
            data-testid="back-to-programs"
          >
            <ArrowLeft size={16} aria-hidden="true" /> {t('programLanding.allPrograms')}
          </button>

          <div className="flex items-start gap-5">
            <div
              className="w-20 h-20 rounded-2xl bg-white flex items-center justify-center shrink-0 shadow-sm"
            >
              <IconFor name={program.icon} color={program.color_primary} size={40} />
            </div>
            <div className="flex-1">
              <h1
                className="text-3xl sm:text-4xl font-duo-display"
                style={{ color: program.color_primary }}
                data-testid="program-hero-title"
              >
                {program.name}
              </h1>
              <p className="mt-3 text-[var(--text-main)] max-w-3xl">{program.description}</p>

              {/* Feature badges */}
              <div className="mt-4 flex flex-wrap gap-2">
                {Object.entries(program.enabled_features || {})
                  .filter(([, v]) => v)
                  .map(([k]) => (
                    <span
                      key={k}
                      className="text-xs font-bold px-3 py-1 rounded-full bg-white/80"
                      style={{ color: program.color_primary }}
                    >
                      {t(`programLanding.feature.${k}`) || k}
                    </span>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Courses section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between mb-6">
          <h2
            className="text-2xl font-duo-display"
            data-testid="program-courses-title"
          >
            {t('programLanding.coursesHeading')}
          </h2>
          <Link
            to={`/courses?program=${slug}`}
            className="inline-flex items-center gap-1 text-sm font-bold hover:underline"
            style={{ color: program.color_primary }}
            aria-label={t('programLanding.coursesAll.ariaLabel').replace('{{name}}', program.name)}
            data-testid="see-all-courses"
          >
            {t('programLanding.coursesAll')} <ArrowRight size={14} aria-hidden="true" />
          </Link>
        </div>

        {/* Courses loading skeleton */}
        {coursesLoading && (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
            aria-label={t('programLanding.coursesLoading')}
            data-testid="program-courses-loading"
          >
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="card-duo animate-pulse h-40" />
            ))}
          </div>
        )}

        {/* Courses error */}
        {!coursesLoading && coursesError && (
          <div className="card-duo p-10 text-center" data-testid="program-courses-error">
            <WifiOff size={36} className="mx-auto text-[var(--text-muted)] mb-3" aria-hidden="true" />
            <p className="font-duo-display text-base text-[var(--text-main)] mb-3">
              {t('programLanding.courses.errorTitle')}
            </p>
            <button
              onClick={() => {
                setCoursesError(false);
                setCoursesLoading(true);
                apiClient.get('/courses', { params: { program_slug: slug } })
                  .then((res) => {
                    const list = res.data?.courses ?? res.data ?? [];
                    setCourses(Array.isArray(list) ? list : []);
                  })
                  .catch(() => setCoursesError(true))
                  .finally(() => setCoursesLoading(false));
              }}
              className="btn-duo-blue"
            >
              {t('common.retry')}
            </button>
          </div>
        )}

        {/* Courses empty */}
        {!coursesLoading && !coursesError && courses.length === 0 && (
          <div
            className="card-duo p-10 text-center"
            data-testid="program-courses-empty"
          >
            <p className="text-[var(--text-muted)] font-bold">{t('programLanding.courses.emptyTitle')}</p>
            <p className="text-xs text-[var(--text-muted)] mt-2">
              {t('programLanding.courses.emptySub')}
            </p>
          </div>
        )}

        {/* Courses grid */}
        {!coursesLoading && !coursesError && courses.length > 0 && (
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
            variants={maybe(reduced, staggerContainer(0.07, 0.05))}
            initial="hidden"
            animate="visible"
          >
            {courses.map((c) => (
              <motion.div
                key={c.course_id}
                variants={maybe(reduced, fadeUp)}
                whileHover={reduced ? undefined : cardHover.hover}
                whileTap={reduced ? undefined : cardHover.tap}
                data-testid={`program-course-card-${c.course_id}`}
              >
                <Link
                  to={`/courses/${c.course_id}`}
                  className="card-duo p-5 h-full flex flex-col block"
                >
                  <h3
                    className="font-duo-display text-lg mb-2"
                    style={{ color: program.color_primary }}
                  >
                    {c.title}
                  </h3>
                  {c.description && (
                    <p className="text-sm text-[var(--text-muted)] line-clamp-3 flex-1">{c.description}</p>
                  )}
                  <div className="mt-4 text-xs text-[var(--text-muted)]">
                    {c.lessons_count
                      ? t('programLanding.lessonsCount').replace('{{count}}', c.lessons_count)
                      : ''}
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
}
